import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiClient } from "../services/api";
import { Class, defaultClass } from "../types/Class";
import { buttonStyle, buttonBackgroundColor } from "../components/Style";
import DeleteConfirmDialog from "../components/DeleteConfirmDialog";
import RoundBox from "../components/RoundBox";
import RoundBoxInput from "../components/RoundBoxInput";
import TimePicker from "../components/TimePicker";

interface Teacher {
  id: number;
  teacherName: string;
}

interface Classroom {
  id: number;
  classroomName: string;
}

const DAYS = [
  { key: "monday", label: "월요일" },
  { key: "tuesday", label: "화요일" },
  { key: "wednesday", label: "수요일" },
  { key: "thursday", label: "목요일" },
  { key: "friday", label: "금요일" },
  { key: "saturday", label: "토요일" },
  { key: "sunday", label: "일요일" },
] as const;

function ClassCardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "new";
  const [form, setForm] = useState<Class>(defaultClass);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [showTimePicker, setShowTimePicker] = useState<{ day: string; type: "start" | "end" } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // 현재 경로에서 클래스 유형 추출
  const getClassCategoryFromRoute = (): "regular" | "special" | "supplement" | undefined => {
    const pathname = window.location.pathname;
    if (pathname.includes("/class/regular")) return "regular";
    if (pathname.includes("/class/special")) return "special";
    if (pathname.includes("/class/supplement")) return "supplement";
    return undefined;
  };

  // 로드
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        
        // 선생님 목록 로드
        const teacherList = await apiClient.request("GET", "/api/classes/data/teachers");
        setTeachers(teacherList);

        // 교실 목록 로드
        const classroomList = await apiClient.request("GET", "/api/classes/data/classrooms");
        setClassrooms(classroomList);

        // 반 정보 로드 (수정 시)
        if (!isNew) {
          const data = await apiClient.getClass(Number(id));
          setForm(data);
        } else {
          // 신규 생성 시 클래스 유형 자동 설정
          const category = getClassCategoryFromRoute();
          if (category) {
            setForm(prev => ({ ...prev, classCategory: category }));
          }
        }
      } catch (e: any) {
        setError(e?.message || "정보 로드 실패");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isNew]);

  const handleSave = async () => {
    try {
      setLoading(true);
      const { id: cid, ...payload } = form;
      if (isNew) {
        await apiClient.createClass(payload);
      } else {
        await apiClient.updateClass(String(cid), payload);
      }
      
      // 라우트에 맞게 리다이렉트
      const category = getClassCategoryFromRoute();
      if (category === "regular") {
        navigate("/class/regular");
      } else if (category === "special") {
        navigate("/class/special");
      } else if (category === "supplement") {
        navigate("/class/supplement");
      } else {
        navigate("/class");
      }
    } catch (e: any) {
      setError(e?.message || "저장 실패");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (isNew || !form.id) return;
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setLoading(true);
      await apiClient.deleteClass(String(form.id));
      
      // 라우트에 맞게 리다이렉트
      const category = getClassCategoryFromRoute();
      if (category === "regular") {
        navigate("/class/regular");
      } else if (category === "special") {
        navigate("/class/special");
      } else if (category === "supplement") {
        navigate("/class/supplement");
      } else {
        navigate("/class");
      }
    } catch (e: any) {
      setError(e?.message || "삭제 실패");
    } finally {
      setLoading(false);
    }
  };

  const handleTimeConfirm = (day: string, type: "start" | "end", time: string) => {
    const timeKey = (type === "start" ? `${day}StartTime` : `${day}EndTime`) as keyof Class;

    setForm((f) => ({
      ...f,
      [timeKey]: time,
    }));

    setShowTimePicker(null);
  };

  const getDayEnabled = (day: string): boolean => {
    const key = day as keyof Class;
    return (form[key] as boolean) ?? false;
  };

  const getDayStartTime = (day: string): string => {
    const key = (`${day}StartTime` as keyof Class);
    const time = (form[key] as string | null) ?? "00:00";
    return time.length > 5 ? time.substring(0, 5) : time;
  };

  const getDayEndTime = (day: string): string => {
    const key = (`${day}EndTime` as keyof Class);
    const time = (form[key] as string | null) ?? "00:00";
    return time.length > 5 ? time.substring(0, 5) : time;
  };

  const formatTo12Hour = (time24: string): string => {
    const [hour, minute] = time24.split(":").map(Number);
    const isPM = hour >= 12;
    let hour12 = hour % 12;
    if (hour12 === 0) hour12 = 12;
    const period = isPM ? "오후" : "오전";
    return `${period} ${String(hour12).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
  };

  return (
    <div style={{ padding: "16px 2.5%", maxWidth: "1200px", margin: "0 auto" }}>
      <h2>반 카드</h2>

      {/* 기본 정보 + 수업 일정 */}
      <RoundBox
        label={isNew ? "반 추가" : "반 정보"}
        direction="vertical"
        style={{ maxWidth: "100%", marginBottom: "24px" }}
        contentStyle={{ gap: 16 }}
      >
        <RoundBoxInput
          label="반 이름"
          value={form.className}
          onChange={(e) => setForm((f) => ({ ...f, className: e.target.value }))}
        />

        {/* 반 종류 라디오 버튼 */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>반 종류</label>
          <div style={{ display: "flex", gap: 16 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input
                type="radio"
                name="classCategory"
                value="regular"
                checked={form.classCategory === "regular" || !form.classCategory}
                onChange={(e) => setForm((f) => ({ ...f, classCategory: e.target.value as "regular" | "special" | "supplement" }))}
              />
              <span>정규반</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input
                type="radio"
                name="classCategory"
                value="special"
                checked={form.classCategory === "special"}
                onChange={(e) => setForm((f) => ({ ...f, classCategory: e.target.value as "regular" | "special" | "supplement" }))}
              />
              <span>특강반</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input
                type="radio"
                name="classCategory"
                value="supplement"
                checked={form.classCategory === "supplement"}
                onChange={(e) => setForm((f) => ({ ...f, classCategory: e.target.value as "regular" | "special" | "supplement" }))}
              />
              <span>보강반</span>
            </label>
          </div>
        </div>

        {/* 특강반 선택 시 시작일/종료일 */}
        {form.classCategory === "special" && (
          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 14, fontWeight: 600, color: "#222", display: "block", marginBottom: 8 }}>특강 시작일</label>
              <input
                type="date"
                value={form.classStartDate || ""}
                onChange={(e) => setForm((f) => ({ ...f, classStartDate: e.target.value }))}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "14px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 14, fontWeight: 600, color: "#222", display: "block", marginBottom: 8 }}>특강 종료일</label>
              <input
                type="date"
                value={form.classEndDate || ""}
                onChange={(e) => setForm((f) => ({ ...f, classEndDate: e.target.value }))}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "14px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        )}

        {/* 보강반 선택 시 보강일 및 시각 */}
        {form.classCategory === "supplement" && (
          <>
            <div>
              <label style={{ fontSize: 14, fontWeight: 600, color: "#222", display: "block", marginBottom: 8 }}>보강일</label>
              <input
                type="date"
                value={form.classStartDate || ""}
                onChange={(e) => setForm((f) => ({ ...f, classStartDate: e.target.value }))}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "14px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#222", display: "block", marginBottom: 8 }}>보강 시작 시각</label>
                <input
                  type="time"
                  value={form.mondayStartTime || ""}
                  onChange={(e) => setForm((f) => ({ ...f, mondayStartTime: e.target.value }))}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "14px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, fontWeight: 600, color: "#222", display: "block", marginBottom: 8 }}>보강 종료 시각</label>
                <input
                  type="time"
                  value={form.mondayEndTime || ""}
                  onChange={(e) => setForm((f) => ({ ...f, mondayEndTime: e.target.value }))}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    fontSize: "14px",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    boxSizing: "border-box",
                  }}
                />
              </div>
            </div>
          </>
        )}

        {/* 반 설명 */}
        <div>
          <label style={{ fontSize: 14, fontWeight: 600, color: "#222", display: "block", marginBottom: 8 }}>반 설명</label>
          <textarea
            value={form.classDescription || ""}
            onChange={(e) => setForm((f) => ({ ...f, classDescription: e.target.value }))}
            style={{
              width: "100%",
              padding: "10px 12px",
              fontSize: "14px",
              border: "1px solid #ddd",
              borderRadius: "6px",
              minHeight: "80px",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
            placeholder="반 설명을 입력해주세요"
          />
        </div>

        {/* 담당 선생님 드롭다운 */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>담당 선생님</label>
          <select
            value={form.teacher || ""}
            onChange={(e) => setForm((f) => ({ ...f, teacher: e.target.value }))}
            style={{
              width: "100%",
              padding: "10px 12px",
              fontSize: "14px",
              border: "1px solid #ddd",
              borderRadius: "6px",
              backgroundColor: "#fff",
              cursor: "pointer",
            }}
          >
            <option value="">선택해주세요</option>
            {teachers.map((teacher) => (
              <option key={teacher.id} value={teacher.teacherName}>
                {teacher.teacherName}
              </option>
            ))}
          </select>
        </div>

        {/* 교실 드롭다운 */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <label style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>교실</label>
          <select
            value={form.classRoom || ""}
            onChange={(e) => setForm((f) => ({ ...f, classRoom: e.target.value }))}
            style={{
              width: "100%",
              padding: "10px 12px",
              fontSize: "14px",
              border: "1px solid #ddd",
              borderRadius: "6px",
              backgroundColor: "#fff",
              cursor: "pointer",
            }}
          >
            <option value="">선택해주세요</option>
            {classrooms.map((classroom) => (
              <option key={classroom.id} value={classroom.classroomName}>
                {classroom.classroomName}
              </option>
            ))}
          </select>
        </div>

        {/* 수업 일정 (보강반이 아닐 때만 표시) */}
        {form.classCategory !== "supplement" && (
          <RoundBox label="수업 일정" direction="vertical" style={{ marginTop: 4 }} contentStyle={{ gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 12 }}>
            {DAYS.map(({ key, label }) => {
              const enabled = getDayEnabled(key);
              const start = getDayStartTime(key);
              const end = getDayEndTime(key);
              return (
                <div
                  key={key}
                  style={{
                    padding: "12px",
                    border: "1px solid #d0d7de",
                    borderRadius: 10,
                    background: enabled ? "#f0f6ff" : "#f9f9f9",
                  }}
                >
                  <div style={{ display: "grid", gridTemplateColumns: "auto auto 1fr 1fr", alignItems: "center", gap: 10 }}>
                    <input
                      type="checkbox"
                      checked={enabled}
                      onChange={(e) => {
                        const dayKey = key as keyof Class;
                        setForm((f) => ({
                          ...f,
                          [dayKey]: e.target.checked,
                        }));
                      }}
                      style={{ width: 16, height: 16, cursor: "pointer" }}
                    />
                    <span style={{ fontSize: 14, fontWeight: 600, color: "#222" }}>{label}</span>

                    <button
                      onClick={() => setShowTimePicker({ day: key, type: "start" })}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        fontSize: 13,
                        fontWeight: 700,
                        borderRadius: 6,
                        border: "1px solid #8aa0ff",
                        background: "#fff",
                        color: "#1b3fa0",
                        cursor: "pointer",
                        opacity: enabled ? 1 : 0.6,
                        transition: "all 0.15s",
                      }}
                    >
                      {formatTo12Hour(start)}
                    </button>

                    <button
                      onClick={() => setShowTimePicker({ day: key, type: "end" })}
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        fontSize: 13,
                        fontWeight: 700,
                        borderRadius: 6,
                        border: "1px solid #8aa0ff",
                        background: "#fff",
                        color: "#1b3fa0",
                        cursor: "pointer",
                        opacity: enabled ? 1 : 0.6,
                        transition: "all 0.15s",
                      }}
                    >
                      {formatTo12Hour(end)}
                    </button>
                  </div>
                </div>
              );
            })}
            </div>
          </RoundBox>
        )}

        {error && <div style={{ color: "#d73a49", marginTop: 4 }}>{error}</div>}

        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={handleSave} disabled={loading} style={{ ...buttonStyle, ...buttonBackgroundColor.save }}>
            저장
          </button>
          {!isNew && (
            <button onClick={handleDeleteClick} disabled={loading} style={{ ...buttonStyle, ...buttonBackgroundColor.delete }}>
              삭제
            </button>
          )}
          <button onClick={() => navigate("/class")} style={{ ...buttonStyle, ...buttonBackgroundColor.cancel }}>
            목록으로
          </button>
        </div>
      </RoundBox>

      {/* TimePicker 모달 */}
      {showTimePicker && (
        <TimePicker
          initialTime={
            showTimePicker.type === "start"
              ? getDayStartTime(showTimePicker.day)
              : getDayEndTime(showTimePicker.day)
          }
          onConfirm={(time) => handleTimeConfirm(showTimePicker.day, showTimePicker.type, time)}
          onCancel={() => setShowTimePicker(null)}
        />
      )}

      <DeleteConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        itemName={form.className}
        itemType="반"
      />
    </div>
  );
}

export default ClassCardPage;

